import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { InfoFooter } from "./InfoPages";

const features = [
  { label: "Premium Streetwear", body: "Standout pieces built to last. Engineered from a full factory tech-pack — never a copy, never a compromise." },
  { label: "Signature Gold Aesthetic", body: "The unmistakable RAW look. Metallic gold stitching on black satin, woven inside the collar." },
  { label: "Fast UK Delivery", body: "From our door to yours, quickly. Tracked shipping across the UK, VAT included at checkout." },
  { label: "Fresh Drops", body: "Small deliberate runs, never restocked after sell-out. New designs across Drop 01, 02, 03 …" },
];

// Public marketing "show card" — self-contained pitch page for ads / DMs / social bios.
// Live rotating hero grid of 4 real products from the shop.
const ShowCard = () => {
  const [products, setProducts] = useState([]);
  const [rotation, setRotation] = useState(0);

  // Pull all published products so we can rotate through them
  useEffect(() => {
    api.get("/products?published_only=true")
      .then((r) => {
        const withImages = (r.data || []).filter((p) => p.image_base64);
        setProducts(withImages);
      })
      .catch(() => setProducts([]));
  }, []);

  // Every 3 seconds, advance the rotation window so a new set of 4 products slides in
  useEffect(() => {
    if (products.length <= 4) return;
    const t = setInterval(() => {
      setRotation((r) => (r + 1) % products.length);
    }, 3000);
    return () => clearInterval(t);
  }, [products.length]);

  // Compute the 4 tiles currently on screen — rotates one product at a time
  // for a smooth ticker feel instead of swapping all 4 at once.
  const tiles = React.useMemo(() => {
    if (products.length === 0) return [];
    const out = [];
    for (let i = 0; i < 4; i++) {
      out.push(products[(rotation + i) % products.length]);
    }
    return out;
  }, [products, rotation]);

  const imgSrc = (b) => (b?.startsWith("data:") ? b : `data:image/png;base64,${b}`);

  return (
    <div data-testid="showcard-page" className="min-h-screen text-white bg-[#0a0a0a]">
      {/* Brand strip */}
      <div className="border-b border-[#d4af37]/25 px-6 lg:px-10 py-4">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-zinc-500">
          <span>// RAW COMPANY CLOTHING · SHOWCASE</span>
          <Link to="/" className="text-[#d4af37] hover:underline">Enter the shop →</Link>
        </div>
      </div>

      {/* Hero — left copy / right rotating product tiles */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="grid-bg absolute inset-0 opacity-30 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-20 grid lg:grid-cols-[1fr_1fr] gap-10 items-center">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-4">
              // PREMIUM UK STREETWEAR · EST. 2026
            </div>
            <h1 className="font-display leading-[0.9] text-5xl sm:text-7xl lg:text-8xl mb-6">
              <span className="text-[#d4af37]">RAW</span> COMPANY <br />
              <span className="text-[#d4af37]">CLOTHING</span>
            </h1>
            <p className="text-lg sm:text-xl text-zinc-300 leading-relaxed max-w-xl mb-4">
              Bold designs, quality fabrics, and a look that turns heads.
            </p>
            <p className="text-sm text-zinc-500 leading-relaxed max-w-xl mb-8">
              Independent UK brand. Premium quality. No mass-market compromise.
              Every piece engineered in-house from a full factory tech-pack,
              with a signature gold thread woven into the inside collar of every unit.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/" data-testid="showcard-shop-cta" className="btn-raw btn-raw-primary">
                → SHOP THE COLLECTION
              </Link>
              <a href="https://rawcompanytech.com" target="_blank" rel="noopener noreferrer me" className="btn-raw">
                PARENT COMPANY ↗
              </a>
            </div>
          </div>

          {/* Rotating 2x2 product grid — cycles every 3s */}
          <div
            data-testid="showcard-rotator"
            className="grid grid-cols-2 gap-3 aspect-square border border-[#d4af37]/40 p-3 bg-black shadow-[0_0_60px_rgba(212,175,55,0.15)]"
          >
            {tiles.length === 0 ? (
              // Fallback if no products yet — show the brand mark
              <div className="col-span-2 row-span-2 flex items-center justify-center">
                <img src="/og-image.png" alt="Raw Company Clothing" className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              tiles.map((p, idx) => (
                <a
                  key={`${p.id}-${rotation}-${idx}`}
                  href={`/product/${p.id}`}
                  data-testid={`showcard-tile-${idx}`}
                  className="relative aspect-square bg-[#050505] overflow-hidden border border-zinc-800 group"
                  style={{ animation: "showcardFade 0.6s ease" }}
                >
                  <img
                    src={imgSrc(p.image_base64)}
                    alt={p.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-2 text-[9px] tracking-[0.2em] uppercase">
                    <div className="text-white truncate">{p.name}</div>
                    <div className="text-[#d4af37]">£{Number(p.price || 0).toFixed(2)}</div>
                  </div>
                </a>
              ))
            )}
          </div>
        </div>
        <style>{`@keyframes showcardFade { from { opacity: 0.3; } to { opacity: 1; } }`}</style>
      </section>

      {/* Feature grid */}
      <section className="border-b border-zinc-800 px-6 lg:px-10 py-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// WHY CHOOSE IT</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-10 max-w-2xl">
            Four pillars that separate us from fast-fashion.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800">
            {features.map((f) => (
              <div key={f.label} className="bg-[#0a0a0a] p-6" data-testid={`showcard-feature-${f.label.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// {f.label}</div>
                <p className="text-sm text-zinc-300 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Landing-page preview — live iframe of the actual homepage */}
      <section className="border-b border-zinc-800 px-6 lg:px-10 py-16 bg-black">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-3">// SEE THE SHOP</div>
          <h2 className="font-display text-3xl sm:text-4xl mb-8 max-w-2xl">
            A quick look at the storefront.
          </h2>
          <div
            data-testid="showcard-landing-preview"
            className="relative w-full border border-[#d4af37]/50 bg-[#0a0a0a] overflow-hidden shadow-[0_0_60px_rgba(212,175,55,0.2)]"
          >
            {/* Browser chrome mock */}
            <div className="flex items-center gap-2 px-4 py-2 border-b border-zinc-800 bg-[#050505]">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
              <span className="ml-4 text-[10px] font-mono text-zinc-500 truncate">https://rawcompanyclothing.uk</span>
            </div>
            <div className="relative aspect-[16/10] overflow-hidden">
              <iframe
                src="/?nochrome=1"
                title="Raw Company Clothing landing page preview"
                className="absolute inset-0 w-full h-full pointer-events-none"
                loading="lazy"
                sandbox="allow-same-origin allow-scripts"
              />
              {/* Clickable overlay so tapping the preview opens the actual shop */}
              <Link
                to="/"
                aria-label="Open the Raw Company Clothing shop"
                className="absolute inset-0 z-10 hover:bg-[#d4af37]/5 transition-colors"
                data-testid="showcard-preview-cta"
              />
            </div>
          </div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-600 mt-4 text-center">
            // Tap the preview to enter the shop
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 lg:px-10 py-16 bg-[#050505]">
        <div className="max-w-[1400px] mx-auto text-center">
          <h2 className="font-display text-4xl sm:text-5xl mb-4">
            Ready to wear the gold standard?
          </h2>
          <p className="text-sm text-zinc-400 mb-8 max-w-xl mx-auto">
            Small drops, deliberate runs. Once a piece is gone, it&apos;s gone.
          </p>
          <Link to="/" data-testid="showcard-cta-bottom" className="btn-raw btn-raw-primary text-base px-8 py-4">
            → SHOP RAW COMPANY CLOTHING
          </Link>
        </div>
      </section>

      {/* Full RAW COMPANY TECH LIMITED footer at the bottom */}
      <InfoFooter />
    </div>
  );
};

export default ShowCard;
